import React, { useState } from "react";
import CommenterImg from "../CommenterImg/CommenterImg";
import { getThemedStyles, styles } from "./CommentEditor.styles";
import { ICommentEditor } from "./CommentEditor.types";
import TextAreaWithAutoHeight from "../../../../common/FormInputs/TextArea/TextAreaWithAutoHeight";
import { classNames } from "../../../../../utils/common/styling";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import Button from "../../../../common/Button/Button";
import { checkIsFunction } from "../../../../../utils/common/dataTypes/function";
import { setHeightOfTextArea } from "../../../../../utils/common/FormInputs/TextArea/TextAreaWithAutoHeight";
import ExlyImage from "../../../../../features/Common/modules/ExlyImage/ExlyImage";
import { useToggleState } from "../../../../../utils/common/dataTypes/boolean";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import AddIcon from "@mui/icons-material/Add";
import PostTypeMediaButton from "../../../PostTypeMediaButton/PostTypeMediaButton";
import { CircularProgress } from "@mui/material";
import LazyLoadedImageViewerModal from "../../../../common/ImageViewerModal/LazyLoadedImageViewerModal";
import { getUserInitials } from "../../../../../features/Common/modules/Comments/utils/Comments";
import { DEFAULT_CREATOR_THEME } from "../../../../../features/Common/modules/Theme/constants/Theme.constants";
import EmojiSelector from "../../../../../features/Common/modules/Comments/modules/EmojiSelector/EmojiSelector";
import AttachmentSelector from "../../../../../features/Common/modules/Comments/modules/AttachmentSelector/AttachmentSelector";

const CommentEditor = ({
  creatorTheme,
  commenterDetails,
  classes = {},
  defaultValue: defaultValueProp,
  inputRef,
  insertTextAtCursor,
  onSubmit,
  emojiSelectorProps,
  attachmentSelectorProps,
  cdnBaseUrl,
  userAvatarPreference,
}: ICommentEditor.ICommentEditorProps) => {
  const themedStyles = getThemedStyles({
    theme: creatorTheme || DEFAULT_CREATOR_THEME,
  });

  const defaultValue = (defaultValueProp || "").trim();
  const [isTextAreaNonEmpty, setIsTextAreaNonEmpty] = useState(!!defaultValue);

  const [isImageViewerModalOpen, openImageViewerModal, closeImageViewerModal] =
    useToggleState(false);

  const [imageViewerModalSrc, setImageViewerModalSrc] = useState("");

  const {
    enableFeature: enableAttachmentSelector,
    attachments = [],
    openImageSelector,
    removeAttachment,
    isUploadingNewItem,
    resetAttachments,
  } = attachmentSelectorProps || { openImageSelector: () => {} };

  const { enableFeature: enableEmojiSelector } = emojiSelectorProps || {};

  const textAreaClassName =
    attachments?.length > 0
      ? styles.textAreaWithAttachmentsRoot
      : styles.textAreaRoot;

  const activatePostCommentButton =
    isTextAreaNonEmpty || attachments?.length > 0;

  const handleTextAreaChange = (event) => {
    const value = event.target.value || "";
    const trimmedValue = value.trim();
    const isNewValueForTextAreaNonEmpty = !!trimmedValue;
    if (isNewValueForTextAreaNonEmpty !== isTextAreaNonEmpty) {
      setIsTextAreaNonEmpty(isNewValueForTextAreaNonEmpty);
    }
  };

  const {
    username = "",
    name = "",
    profilePicUrl = "",
  } = commenterDetails || {};

  const TextAreaExtras = (
    <div className={styles.extras}>
      {enableAttachmentSelector && (
        <AttachmentSelector openImageFileSelector={openImageSelector} />
      )}
      {enableEmojiSelector && insertTextAtCursor && (
        <EmojiSelector
          handleEmojiSelect={(value) => {
            insertTextAtCursor(value);
            setIsTextAreaNonEmpty(true);
          }}
        />
      )}
    </div>
  );

  return (
    <div className={classNames(styles.container, classes.container)}>
      <CommenterImg
        avatarPreference={userAvatarPreference}
        userDetails={{
          profilePic: profilePicUrl,
          username,
          initials: getUserInitials(name),
        }}
      />

      <div className={styles.inputContainer}>
        <TextAreaWithAutoHeight
          placeholder="Add a comment"
          inputRef={inputRef as React.ForwardedRef<HTMLTextAreaElement>}
          autoFocus
          classes={{ root: textAreaClassName }}
          rows={1}
          defaultValue={defaultValue}
          onChange={handleTextAreaChange}
          extras={TextAreaExtras}
        />
        {attachments?.length > 0 && (
          <div className={styles.attachments}>
            {attachments.map((attachment, index) => (
              <div
                className={styles.imageContainer}
                key={attachment.url + index}
              >
                <PostTypeMediaButton
                  Icon={CloseRoundedIcon}
                  onClick={() => removeAttachment?.(index)}
                  className={styles.removeImageButton}
                />
                <ExlyImage
                  onClick={() => {
                    setImageViewerModalSrc(attachment.url);
                    openImageViewerModal();
                  }}
                  src={attachment.url}
                  key={attachment.url + index}
                  fetchWidth={200}
                  className={styles.image}
                  cdnBaseUrl={cdnBaseUrl || ""}
                />
              </div>
            ))}
            <div
              className={styles.newAttachmentPlaceholder}
              onClick={() => {
                if (isUploadingNewItem) return;
                openImageSelector();
              }}
            >
              {isUploadingNewItem ? (
                <CircularProgress color="inherit" />
              ) : (
                <AddIcon className={styles.addIcon} />
              )}
            </div>
          </div>
        )}
      </div>
      {activatePostCommentButton && (
        <Button
          color="unset"
          size="unset"
          onClick={() => {
            if (checkIsFunction(onSubmit)) {
              onSubmit({
                comment_body: inputRef.current?.value || "",
                attachments,
                successCallback: () => {
                  setIsTextAreaNonEmpty(false);
                  if (resetAttachments) resetAttachments();
                  if (inputRef?.current) {
                    inputRef.current.value = "";
                    setHeightOfTextArea(
                      inputRef.current as HTMLTextAreaElement,
                    );
                  }
                },
              });
            }
          }}
          className={styles.postCommentBtn}
        >
          <SendRoundedIcon
            className={classNames(
              styles.postCommentBtnIcon,
              themedStyles.postCommentBtnIcon,
            )}
          />
        </Button>
      )}
      <LazyLoadedImageViewerModal
        isOpen={isImageViewerModalOpen}
        onClose={() => {
          closeImageViewerModal();
          setImageViewerModalSrc("");
        }}
        imgSrc={imageViewerModalSrc}
        cdnBaseUrl={cdnBaseUrl}
      />
    </div>
  );
};

export default CommentEditor;
